import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings, 
  Filter, 
  Search,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  User,
  Bell,
  RefreshCw,
  MessageSquare,
  BarChart3,
  Activity,
  Zap,
  Target,
  Calendar,
  Users,
  Mail,
  Phone,
  Monitor,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import MockDataService from '../../services/MockDataService';

const AutomationRules = () => {
  const [automationRules, setAutomationRules] = useState([]);
  const [triggerTypes, setTriggerTypes] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testingRule, setTestingRule] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesResponse, triggersResponse, actionsResponse] = await Promise.all([
        MockDataService.getAutomationRules(),
        MockDataService.getTriggerTypes(),
        MockDataService.getActionTypes()
      ]);

      if (rulesResponse.success) {
        // Ensure data is always an array
        const rulesData = Array.isArray(rulesResponse.data) ? rulesResponse.data : [];
        setAutomationRules(rulesData);
      } else {
        setError(rulesResponse.message || 'Failed to load automation rules');
        setAutomationRules([]); // Set empty array on error
      }

      if (triggersResponse.success) {
        setTriggerTypes(Array.isArray(triggersResponse.data) ? triggersResponse.data : []);
      } else {
        setTriggerTypes([]);
      }

      if (actionsResponse.success) {
        setActionTypes(Array.isArray(actionsResponse.data) ? actionsResponse.data : []);
      } else {
        setActionTypes([]);
      }
    } catch (err) {
      console.error('Error loading automation rules:', err);
      setError('Failed to load automation rules data');
      // Set empty arrays on error to prevent crashes
      setAutomationRules([]);
      setTriggerTypes([]);
      setActionTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async (ruleData) => {
    try {
      const response = await MockDataService.createAutomationRule(ruleData);
      if (response.success) {
        await loadData();
        setShowCreateForm(false);
      }
    } catch (err) {
      setError('Failed to create automation rule');
    }
  };

  const handleUpdateRule = async (ruleId, ruleData) => {
    try {
      const response = await MockDataService.updateAutomationRule(ruleId, ruleData);
      if (response.success) {
        await loadData();
        setEditingRule(null);
      }
    } catch (err) {
      setError('Failed to update automation rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this automation rule?')) {
      try {
        const response = await MockDataService.deleteAutomationRule(ruleId);
        if (response.success) {
          await loadData();
        }
      } catch (err) {
        setError('Failed to delete automation rule');
      }
    }
  };

  const handleTestRule = async (ruleId) => {
    setTestingRule(ruleId);
    try {
      const response = await MockDataService.testAutomationRule(ruleId, {});
      if (response.success) {
        alert('Rule test completed successfully!');
      }
    } catch (err) {
      setError('Failed to test automation rule');
    } finally {
      setTestingRule(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'bg-red-100 text-red-800';
      case 2: return 'bg-orange-100 text-orange-800';
      case 3: return 'bg-yellow-100 text-yellow-800';
      case 4: return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Icon mapping: string names to React components
  const iconMap = {
    'Zap': Zap,
    'Settings': Settings,
    'Bell': Bell,
    'Mail': Mail,
    'MessageSquare': MessageSquare,
    'Phone': Phone,
    'User': User,
    'Users': Users,
    'Target': Target,
    'Calendar': Calendar,
    'Clock': Clock,
    'Monitor': Monitor,
    'Activity': Activity,
    'CheckCircle': CheckCircle,
    'AlertTriangle': AlertTriangle,
    'TrendingUp': TrendingUp,
    'BarChart3': BarChart3,
    'Search': Search,
    'Filter': Filter,
    'Play': Play,
    'Pause': Pause,
    'RefreshCw': RefreshCw,
    'Refresh': RefreshCw, // Alias
    'Plus': Plus,
    'Edit': Edit,
    'Trash2': Trash2,
    'Tag': Tag,
    'bell': Bell, // Lowercase variants
    'mail': Mail,
    'tag': Tag,
    'refresh': RefreshCw
  };

  const getTriggerIcon = (triggerType) => {
    const triggerTypeData = triggerTypes.find(t => t.id === triggerType);
    const iconName = triggerTypeData?.icon || 'Zap';
    // Return the actual React component, not a string
    return iconMap[iconName] || Zap;
  };

  const getActionIcon = (actionType) => {
    const actionTypeData = actionTypes.find(a => a.id === actionType);
    const iconName = actionTypeData?.icon || 'Settings';
    // Return the actual React component, not a string
    return iconMap[iconName] || Settings;
  };

  const filteredRules = (Array.isArray(automationRules) ? automationRules : []).filter(rule => {
    if (!rule) return false;
    const ruleName = (rule.name || '').toLowerCase();
    const ruleDescription = (rule.description || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = ruleName.includes(searchLower) || ruleDescription.includes(searchLower);
    const matchesStatus = statusFilter === 'all' || rule.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Automation Rules</h1>
          <p className="text-gray-600">Create and manage automation rules for ticket processing</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Rule
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Rules</p>
                <p className="text-2xl font-bold text-gray-900">{automationRules.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Rules</p>
                <p className="text-2xl font-bold text-gray-900">
                  {automationRules.filter(r => r.status === 'active').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Executions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {automationRules.reduce((sum, r) => sum + (r.statistics?.executions || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {automationRules.length > 0 
                    ? Math.round(automationRules.reduce((sum, r) => sum + (r.statistics?.successRate || 0), 0) / automationRules.length)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search automation rules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      <div className="space-y-4">
        {filteredRules && filteredRules.length > 0 ? filteredRules.map((rule) => (
          <Card key={rule.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{rule.name || 'Unnamed Rule'}</h3>
                    <Badge className={getStatusColor(rule.status || 'inactive')}>
                      {rule.status || 'inactive'}
                    </Badge>
                    <Badge className={getPriorityColor(rule.priority || 4)}>
                      Priority {rule.priority || 4}
                    </Badge>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{rule.description || 'No description provided'}</p>

                  {/* Triggers */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Triggers:</h4>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(rule.triggers) ? rule.triggers : []).map((trigger) => {
                        if (!trigger) return null;
                        const IconComponent = getTriggerIcon(trigger.type);
                        return (
                          <div key={trigger.id || Math.random()} className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
                            <IconComponent className="h-4 w-4 text-blue-600" />
                            <span className="text-sm text-blue-800">{(trigger.type || '').replace('_', ' ')}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Actions:</h4>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(rule.actions) ? rule.actions : []).map((action) => {
                        if (!action) return null;
                        const IconComponent = getActionIcon(action.type);
                        return (
                          <div key={action.id || Math.random()} className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full">
                            <IconComponent className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-800">{(action.type || '').replace('_', ' ')}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Executions:</span>
                      <span className="ml-1 font-medium">{rule.statistics?.executions || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Success Rate:</span>
                      <span className="ml-1 font-medium">{rule.statistics?.successRate || 0}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Last Executed:</span>
                      <span className="ml-1 font-medium">
                        {rule.statistics?.lastExecuted 
                          ? new Date(rule.statistics.lastExecuted).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Avg Time:</span>
                      <span className="ml-1 font-medium">{rule.statistics?.averageExecutionTime || 0}ms</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestRule(rule.id)}
                    disabled={testingRule === rule.id}
                    className="flex items-center gap-1"
                  >
                    <Play className="h-4 w-4" />
                    {testingRule === rule.id ? 'Testing...' : 'Test'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingRule(rule)}
                    className="flex items-center gap-1"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Automation Rules Found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters to see more results.'
                  : 'Get started by creating your first automation rule.'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2 mx-auto">
                  <Plus className="h-4 w-4" />
                  Create Rule
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Rule Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Automation Rule</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const ruleData = {
                name: formData.get('name'),
                description: formData.get('description'),
                priority: parseInt(formData.get('priority')),
                triggers: [],
                actions: [],
                conditions: {
                  enabled: true,
                  schedule: formData.get('schedule'),
                  exceptions: []
                }
              };
              handleCreateRule(ruleData);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                  <Input name="name" required />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      name="priority"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="1">1 - Highest</option>
                      <option value="2">2 - High</option>
                      <option value="3">3 - Medium</option>
                      <option value="4">4 - Low</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                    <select
                      name="schedule"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="always">Always</option>
                      <option value="business_hours">Business Hours</option>
                      <option value="weekdays">Weekdays Only</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Rule</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Rule Modal */}
      {editingRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Automation Rule</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const ruleData = {
                name: formData.get('name'),
                description: formData.get('description'),
                priority: parseInt(formData.get('priority')),
                status: formData.get('status'),
                triggers: Array.isArray(editingRule.triggers) ? editingRule.triggers : [],
                actions: Array.isArray(editingRule.actions) ? editingRule.actions : [],
                conditions: {
                  enabled: true,
                  schedule: formData.get('schedule') || (editingRule.conditions?.schedule || 'always'),
                  exceptions: []
                }
              };
              handleUpdateRule(editingRule.id, ruleData);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                  <Input name="name" defaultValue={editingRule.name} required />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    defaultValue={editingRule.description}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      name="priority"
                      defaultValue={editingRule.priority}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="1">1 - Highest</option>
                      <option value="2">2 - High</option>
                      <option value="3">3 - Medium</option>
                      <option value="4">4 - Low</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      defaultValue={editingRule.status}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                    <select
                      name="schedule"
                      defaultValue={editingRule.conditions?.schedule || 'always'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="always">Always</option>
                      <option value="business_hours">Business Hours</option>
                      <option value="weekdays">Weekdays Only</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingRule(null)}
                >
                  Cancel
                </Button>
                <Button type="submit">Update Rule</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
};

export default AutomationRules;
